import { X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useBottomSheetStore } from '../store/bottom-sheet-store'

/** Keep a sheet's content mounted for `ms` after `open` flips false, so it slides out WITH
 *  the panel instead of blanking instantly mid-animation. Companion to MobileBottomSheet's
 *  200ms translate transition. */
export function useExitMount(open: boolean, ms = 240): boolean {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    const t = setTimeout(() => setMounted(false), ms)
    return () => clearTimeout(t)
  }, [open, ms])
  return mounted
}

interface MobileBottomSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  /** Optional pinned footer slot (e.g. a chat-style input). */
  footer?: ReactNode
  /**
   * 'full' (default) covers the viewport minus a top sliver. 'half' covers the
   * bottom half with a light scrim, keeping the content behind it readable.
   */
  height?: 'full' | 'half'
  /**
   * 'raised' stacks this sheet ABOVE another open sheet (scrim z-[59] / panel z-[60] —
   * the same stacking the mobile CompositionDock uses). Default 'base' is z-40/z-50.
   */
  zIndex?: 'base' | 'raised'
  children: ReactNode
}

export function MobileBottomSheet({
  open,
  onClose,
  title,
  footer,
  height = 'full',
  zIndex = 'base',
  children,
}: MobileBottomSheetProps) {
  // Bump the global open-count while this sheet is up so floating app chrome
  // (e.g. the home page's chat bar) can hide itself.
  useEffect(() => {
    if (!open) return
    const { increment, decrement } = useBottomSheetStore.getState()
    increment()
    return () => decrement()
  }, [open])

  // Render through a portal to document.body so `position: fixed` always
  // anchors to the visual viewport. Without this, any ancestor with a
  // transform/filter/will-change/contain becomes the containing block for
  // the sheet, leaving it shorter than the viewport — which is exactly what
  // happened when the sheet was nested inside TopicSwitcher (vs. rendered
  // as a direct child of the home page like the AssistantChat sheet).
  if (typeof document === 'undefined') return null

  const half = height === 'half'
  const raised = zIndex === 'raised'
  return createPortal(
    <>
      <div
        className={`fixed inset-0 ${raised ? 'z-[59]' : 'z-40'} transition-opacity duration-200 ${half ? 'bg-black/30' : 'bg-black/70 backdrop-blur-sm'} ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
      />
      <div
        className={`pointer-events-none fixed inset-0 ${raised ? 'z-[60]' : 'z-50'} transition-transform duration-200 ${open ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div
          className={`pointer-events-auto absolute inset-x-0 bottom-0 rounded-t-[16px] border-t border-line bg-bg-2 text-paper shadow-[var(--shadow-card)] ${half ? 'top-1/2' : 'top-6'}`}
        >
          <div className="flex h-full flex-col">
            <div className="flex items-center border-b border-line px-4 py-3">
              {title && (
                <h2 className="flex-1 text-[14px] font-semibold tracking-[-0.005em] text-paper">
                  {title}
                </h2>
              )}
              {!title && <div className="flex-1" />}
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex-shrink-0 rounded-[8px] p-1 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
            {footer && <div className="flex-shrink-0 border-t border-line">{footer}</div>}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
